import { PkEmptyMessage, PkTab, PkTabPanel, PkTabs } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetStandingsQuery } from 'api/fspApi';
import { DomesticTournament } from 'components/DomesticTournament';
import { Standings } from 'components/Standings';
import useIsDesktop from 'hooks/useIsDesktop';
import { useMatchData } from 'hooks/useMatchData';
import config from 'utils/config';
import { translateEntity } from 'utils/translateEntity';

export const StandingsTab = () => {
  const { competition: competitionConfig, matchData } = useMatchData();

  const { t } = useTranslation();
  const isDesktop = useIsDesktop();

  const competition =
    config.competitions.find((item) => item.id === matchData.competition.id) || config.competitionsDefault;

  const isClubCompetition = competition.type !== 'national';

  const Domestic = useMemo(() => {
    if (!matchData?.homeTeam || !matchData?.awayTeam) {
      return <PkEmptyMessage className="pr-font-size--14 ">{t('table_is_not_available')}</PkEmptyMessage>;
    } else {
      return (
        <DomesticTournament
          awayTeam={matchData.awayTeam}
          homeTeam={matchData.homeTeam}
          isDesktop={isDesktop}
          seasonYear={matchData.seasonYear}
        />
      );
    }
  }, [isDesktop, matchData.awayTeam, matchData.homeTeam, matchData.seasonYear, t]);

  const { data: standings = [], isLoading: standingsLoading } = useGetStandingsQuery({
    competitionId: competitionConfig.id,
    seasonYear: matchData.seasonYear,
    teamId: `${matchData.awayTeam?.id},${matchData.homeTeam?.id}`,
    phase: matchData.round?.phase ?? 'TOURNAMENT',
  });

  const isTwoTeamInDifferentCompetitionStandings = useMemo(() => {
    if (standings.length === 1) {
      const filtered = standings[0].items.filter((item) => {
        if (matchData.awayTeam?.id === item.team.id || matchData.homeTeam?.id === item.team.id) {
          return true;
        }
      });

      return filtered.length === 1;
    }

    return false;
  }, [matchData.awayTeam?.id, matchData.homeTeam?.id, standings]);

  const seasonYearsName = useMemo(() => {
    const previousYear = parseInt(matchData.seasonYear, 10) - 1;
    const allowedTournamentWithYears = config.connectedCompetition[0];

    if (allowedTournamentWithYears.includes(competitionConfig.id)) {
      return `${previousYear}/${matchData.seasonYear}`;
    }

    return '';
  }, [competitionConfig.id, matchData.seasonYear]);

  const isStandingsAvailable = standings.length === 0;

  const tableName =
    matchData.competition.id === '17'
      ? translateEntity(matchData?.competition)?.qualifyingName
      : translateEntity(matchData?.competition)?.tournamentName;

  if (standingsLoading) {
    return <div />;
  }

  return (
    // TODO: refactor layout to use a common template for tabs

    <div className="pk-container">
      <div className="pk-col pk-col--span-12">
        <div
          className={clsx(
            'pk-line-height--sm pk-font-style--normal pk-text--text-01 pk-font--comp-bold pk-font-size--xl2',
            isDesktop && 'pk-font-size--xl3',
          )}
        >
          {t('standings')}
        </div>
      </div>
      <div className="pk-col pk-col--span-12">
        <PkTabs>
          <PkTab tab-id="comp-tab" selected>
            {tableName} {seasonYearsName}
          </PkTab>
          {isClubCompetition && <PkTab tab-id="domestic-tab">{t('domestics')}</PkTab>}

          <PkTabPanel tab="comp-tab" slot="content">
            {standings.map((standing, key) => (
              <div className="pk-col pk-col--span-12 pk-mt--l pk-mt-md--xl" key={key}>
                <div className="pk-mt--0 pk-mb--0 pk-mb-md--l">
                  <Standings teamStandings={standing} competition={competition} match={matchData} />
                </div>
              </div>
            ))}
            {isStandingsAvailable && (
              <div className="pk-col pk-col--span-12 pk-mt--0 pk-mb--0 pk-mb-md--l">
                <PkEmptyMessage className="pr-font-size--14">{t('table_is_not_available')}</PkEmptyMessage>
              </div>
            )}
            {isTwoTeamInDifferentCompetitionStandings && (
              <div className="pk-col pk-col--span-12 pk-mt--0 pk-mb--0 pk-mb-md--l">
                <PkEmptyMessage className="pr-font-size--14 ">
                  {t('standings_not_available_for_teams_from_different_competition')}
                </PkEmptyMessage>
              </div>
            )}
          </PkTabPanel>

          {isClubCompetition && (
            <PkTabPanel tab="domestic-tab" slot="content">
              <div className="pk-col pk-col--span-12 pk-mt--l pk-mt-md--xl">{Domestic}</div>
            </PkTabPanel>
          )}
        </PkTabs>
      </div>
    </div>
  );
};
